import { Form, Input } from "antd"
import dayjs from "dayjs"
import { motion } from "motion/react"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"

import { curriculumAtoms } from "#atoms"
import { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import Grid from "#blocks/Grid"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import {
  AttendanceClassSessions,
  AttendanceStatus,
  invariant,
} from "#customTypes/types"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { getNow, isTodayOrFuture } from "#utils/dateHelpers"
import { type FeeBreakdown } from "#utils/feeCalculationHelpers"
import { getSkillLevelForClassGroup } from "#utils/studentSkillHelpers"
import { camelToWords } from "#utils/textFormatters"

import FeeSelector from "./FeeSelector"

const ReceiptItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <Flex col gap={4}>
      <motion.h3 {...ANIMATION_BY_KEY.leftSm} key={label}>
        {label}
      </motion.h3>
      <motion.div {...ANIMATION_BY_KEY.leftSm} key={value}>
        {value}
      </motion.div>
    </Flex>
  )
}

const ReceiptInput = ({
  name,
  label,
  placeholder,
  required,
  defaultValue,
}: {
  name: string
  label: string
  placeholder: string
  required?: boolean
  defaultValue?: string
}) => {
  const theme = useThemeContext()
  const form = Form.useFormInstance()

  useLayoutEffect(() => {
    if (defaultValue) {
      form.setFieldValue(name, defaultValue)
    }
  }, [form, defaultValue, name])

  return (
    <motion.div {...ANIMATION_BY_KEY.leftSm} key={name}>
      <Flex style={{ marginBottom: 4 }}>
        {label}
        {required && <span style={{ color: theme.error }}>*</span>}
      </Flex>
      <Form.Item
        name={name}
        style={{ marginBottom: 0 }}
        rules={required ? [{ required: true, message: "Required field" }] : []}
      >
        <Input placeholder={placeholder} style={{ width: "50%" }} />
      </Form.Item>
    </motion.div>
  )
}

const isValidOptOutDate = (status: AttendanceStatus) => {
  return (
    status === ATTENDANCE_STATUSES.NOT_ENROLLED ||
    status === ATTENDANCE_STATUSES.ENROLLED ||
    status === ATTENDANCE_STATUSES.INVALID
  )
}

// Check if a session is a tryout or makeup (should NOT be counted for pricing)
const isTryoutOrMakeup = (status: AttendanceStatus) => {
  return (
    status === ATTENDANCE_STATUSES.TRYOUT_PRESENT ||
    status === ATTENDANCE_STATUSES.TRYOUT_ABSENT ||
    status === ATTENDANCE_STATUSES.TRYOUT_TARDY ||
    status === ATTENDANCE_STATUSES.TRYOUT_ENROLLED ||
    status === ATTENDANCE_STATUSES.MAKEUP_PRESENT ||
    status === ATTENDANCE_STATUSES.MAKEUP_ABSENT ||
    status === ATTENDANCE_STATUSES.MAKEUP_TARDY ||
    status === ATTENDANCE_STATUSES.MAKEUP_ENROLLED
  )
}

const AddStudentReceipt = ({
  student,
  selectedSessions,
  attendanceForClass,
  sortedClassSessions,
  netSessionCount,
  curriculumId: curriculumIdProp,
  classData,
}: {
  student: StudentConstructor | null
  selectedSessions: boolean[]
  attendanceForClass: AttendanceClassSessions
  sortedClassSessions: ClassSessionConstructor[]
  netSessionCount: number
  curriculumId?: string
  classData?: {
    isSummerCamp?: boolean
    startDate?: string | number | null
    name?: string
    curriculumId?: string
    semester?: string
  } | null
}) => {
  const form = Form.useFormInstance()

  // Use class data from props if available, otherwise fall back to form context
  // (form context exists when rendered inside AddClassForm)
  const isSummerCampFromForm = Form.useWatch<boolean>("isSummerCamp", form)
  const startDateFromForm = Form.useWatch<string>("startDate", form)
  const classNameFromForm = Form.useWatch<string>("name", form)
  const curriculumIdFromForm = Form.useWatch<string>("curriculumId", form)
  const semesterFromForm = Form.useWatch<string>("semester", form)

  const isSummerCamp = classData?.isSummerCamp ?? isSummerCampFromForm
  const startDate = classData?.startDate ?? startDateFromForm
  const className = classData?.name ?? classNameFromForm
  const semester = classData?.semester ?? semesterFromForm
  const curriculumId =
    curriculumIdProp ?? classData?.curriculumId ?? curriculumIdFromForm
  const curriculum = useOneDoc<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    curriculumId
  )

  const [feeBreakdown, setFeeBreakdown] = useState<FeeBreakdown | null>(null)

  // Calculate opt out dates
  const optOutDates = useMemo(() => {
    const firstSelectedIndex = selectedSessions.findIndex(Boolean)
    const lastSelectedIndex = selectedSessions.findLastIndex(Boolean)

    if (firstSelectedIndex === -1 || lastSelectedIndex === -1) return ""

    const skippedDates: string[] = []

    for (let i = firstSelectedIndex + 1; i < lastSelectedIndex; i++) {
      const status =
        attendanceForClass[i]?.status ?? ATTENDANCE_STATUSES.INVALID
      // If session is not selected and is valid to drop
      if (!selectedSessions[i] && isValidOptOutDate(status)) {
        const session = sortedClassSessions[i]
        if (session) {
          const skipValue = isSummerCamp ? "day" : "week"
          const sessionDate = dayjs(startDate).add(i, skipValue).format("M/D")
          skippedDates.push(sessionDate)
        }
      }
    }

    return skippedDates.join(", ")
  }, [
    selectedSessions,
    attendanceForClass,
    sortedClassSessions,
    startDate,
    isSummerCamp,
  ])

  // Update form field when optOutDates changes
  useEffect(() => {
    form.setFieldValue("optOutDates", optOutDates)
  }, [form, optOutDates])

  // Update form with fee breakdown for receipt
  useEffect(() => {
    if (feeBreakdown) {
      // Store the grand total as payment amount
      form.setFieldValue("paymentAmount", `$${feeBreakdown.grandTotal}`)
      // Store the full breakdown for structured receipt
      form.setFieldValue("feeBreakdown", feeBreakdown)
    }
  }, [feeBreakdown, form])

  // Calculate total sessions remaining in semester (future sessions)
  const totalSessionsRemaining = useMemo(() => {
    const now = getNow().startOf("day")
    return sortedClassSessions.filter((session) => {
      const sessionDate = dayjs(session.eventStart).startOf("day")
      return isTodayOrFuture(sessionDate, now)
    }).length
  }, [sortedClassSessions])

  // Handle fee breakdown changes
  const handleFeeBreakdownChange = useCallback((breakdown: FeeBreakdown) => {
    console.log("🧾 handleFeeBreakdownChange => ", breakdown)
    setFeeBreakdown(breakdown)
  }, [])

  if (!curriculum) return <div>Curriculum not found</div>

  const classGroup = curriculum?.group

  invariant(
    classGroup != null,
    `Curriculum ${curriculumId} is missing a class group!`
  )

  const skillLevelForFees =
    getSkillLevelForClassGroup(student, classGroup, semester || undefined) ||
    undefined

  const studentLevel = skillLevelForFees ?? "..."

  const skipValue = isSummerCamp ? "day" : "week"

  const sessionsData = selectedSessions.map((isSelected, i) => ({
    sessionIndex: i,
    startDate: dayjs(startDate).add(i, skipValue).format("M/D"),
    isSelected,
  }))

  const firstDay = sessionsData.find((session) => session.isSelected)?.startDate
  const lastDay = sessionsData.findLast(
    (session) => session.isSelected
  )?.startDate

  // Number of sessions for pricing (excludes tryouts and makeups)
  const sessionCount = selectedSessions.filter(
    (isSelected, i) =>
      isSelected && !isTryoutOrMakeup(attendanceForClass[i]?.status)
  ).length

  return (
    <Flex col gap={30}>
      {/* Student & Class Info */}
      <Grid columns={3} columnGap={30} rowGap={30}>
        <ReceiptItem
          label="Student"
          value={
            student
              ? `${student.firstName} ${student.lastName}`
              : "No student selected"
          }
        />
        <ReceiptItem label="DOB" value={student ? `${student.dob}` : "..."} />
        <ReceiptItem
          label={`${camelToWords(classGroup)} skill level`}
          value={`${studentLevel ?? "NA"}`}
        />

        <ReceiptItem label="Class name" value={`${className}`} />
        <ReceiptItem
          label="Session dates"
          value={
            firstDay && lastDay
              ? `${firstDay} - ${lastDay}`
              : "No sessions selected"
          }
        />
        <ReceiptItem
          label="Sessions"
          value={
            netSessionCount > 0
              ? `+${netSessionCount}`
              : netSessionCount < 0
                ? `${netSessionCount}`
                : "0"
          }
        />
      </Grid>

      {/* Opt Out Dates */}
      <ReceiptInput
        name="optOutDates"
        label="Opt out dates"
        placeholder="Example: 1/1/25, 1/2/25"
      />

      {/* Fee Selector */}
      <FeeSelector
        classGroup={classGroup}
        skillLevel={skillLevelForFees}
        curriculumSkillLevels={curriculum.skillLevels}
        sessionCount={sessionCount}
        netSessionCount={netSessionCount}
        totalSessionsRemaining={totalSessionsRemaining}
        onFeeBreakdownChange={handleFeeBreakdownChange}
      />
    </Flex>
  )
}

export default AddStudentReceipt
